import { useState, useEffect } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from 'lib/firebase';
import { USERS } from '../ChoresTouch/choreConstants';
import { calculateChoreState } from '../ChoresTouch/choreUtils';

/**
 * Custom hook to fetch real-time qualification status for all family members
 * 
 * OPTIMIZED: Uses 2 global listeners instead of N individual listeners.
 * 1. Listens to all 'chores'
 * 2. Listens to all 'fixed_chore_status'
 * Calculates qualification state in-memory.
 */
export const useFamilyData = () => {
    const [dbChores, setDbChores] = useState([]);
    const [fixedStatuses, setFixedStatuses] = useState({});
    const [loadingChores, setLoadingChores] = useState(true);
    const [loadingFixed, setLoadingFixed] = useState(true);

    // 1. Fetch All Chores (Once)
    useEffect(() => {
        const q = query(collection(db, "chores"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
             const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
             setDbChores(items);
             setLoadingChores(false);
        }, (error) => {
            console.error("Error fetching chores:", error);
            setLoadingChores(false);
        });
        return () => unsubscribe();
    }, []);

    // 2. Fetch All Fixed Statuses (Once)
    useEffect(() => {
        const q = query(collection(db, "fixed_chore_status"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const statuses = {};
            snapshot.forEach(doc => {
                statuses[doc.id] = doc.data();
            });
            setFixedStatuses(statuses);
            setLoadingFixed(false);
        }, (error) => {
            console.error("Error fetching fixed statuses:", error);
            setLoadingFixed(false);
        });
        return () => unsubscribe();
    }, []);

    const loading = loadingChores || loadingFixed;

    // 3. Calculate Derived State
    const familyData = USERS.map(user => {
        const userFixedStatus = fixedStatuses[user.id] || {};
        
        // Use pure function to calculate status
        // We pass empty object for optimistic params as we don't need instant drag feedback here
        const { qualificationStatus } = calculateChoreState(
            user.id, 
            dbChores, 
            userFixedStatus
        );
        
        return {
            ...user,
            qualified: qualificationStatus.qualified
        };
    });

    return { familyData, loading };
};
